import { useEffect, useMemo, useState } from "react";
import { colors, fonts, glyphs } from "../../../../../Styles";
import { NewButton, GenericDeviceTable, ResetTableModal, SecondaryPopover, ResetTableClearQueueModal } from "../../../../../Components";
import { ButtonTypes, ListData_ValueTypes, LC_TABLE_IDS } from "../../../../../Definitions";
import { LC_FEED_DAY, LCFeedDayArgs, LCFeedDayResponse } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/lcFeedDay";
import { useLazyQuery } from "@apollo/client";
import { useAicoConnection } from "../../../../../Components/ToolsComponents/_ToolsComponents/aicoConnectionContext";

const columnsData = [
	{ headerValue: "ID", accessor: "id", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "milestoneId", accessor: "milestoneId", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "feedcurveId", accessor: "feedcurveId", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "day", accessor: "day", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "nDay", accessor: "nDay", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "compCnt", accessor: "compCnt", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "feedcurve_day_components", accessor: "feedcurve_day_components", type: ListData_ValueTypes.Div, sort: false },
]

export default function FeedDayDataTable() {
	const [isResetTableModalOpen, setIsResetTableModalOpen] = useState<boolean>(false);
	const [isResetTableClearQueueModalOpen, setIsResetTableClearQueueModalOpen] = useState<boolean>(false);

	const { state: connectionState } = useAicoConnection();

	const [fetchData, {data, loading, error }] = useLazyQuery<LCFeedDayResponse, LCFeedDayArgs>(LC_FEED_DAY, { fetchPolicy: "network-only" });

	useEffect(() => {
		if (connectionState.deviceConnection) {
			fetchData({variables: connectionState.deviceConnection});
		}
	}, [connectionState.deviceConnection]);

	const _labelStyle = {...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, ...fonts.fontWeight2, whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "94px", minHeight: "15px", overflow: "hidden"};
	const _valueStyle = { ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, minHeight: "15px" };

	const tableData = useMemo(() => {
		return data?.controller.lcFeedDay?.map((e, i) => {
			let componentCell: React.ReactNode | undefined = undefined;

			if (e.feedcurve_day_components.length) {
				componentCell = (
					<div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
						<SecondaryPopover
							content={<glyphs.Note fillColor={colors.greyDarkPrimary} />}
							popoverHeader={"Components"}
							popoverContent={<>
								<div style={{ display: "grid", gridTemplateColumns: "94px 2px 94px", gridTemplateRows: "auto", gap: "10px", width: "100%" }}>
									<div style={{ display: "flex", alignItems: "start", justifyContent: "end", ..._labelStyle }}> Component Id </div>
									<div style={{ width: "2px", height: "100%", backgroundColor: colors.greyDarkSecondary, opacity: "0.5" }} />
									<div style={{display: "flex", alignItems: "start", justifyContent: "start", height: "18px", ..._labelStyle}}>Amount</div>
								</div>
								{e.feedcurve_day_components.map((dayComponent, index) =>
									<div key={index} style={{ display: "grid", gridTemplateColumns: "94px 2px 94px", gridTemplateRows: "auto", gap: "10px", width: "100%" }}>
										<div style={{ display: "flex", alignItems: "start", justifyContent: "end", ..._valueStyle }}> {dayComponent.componentId} </div>
										<div style={{ width: "2px", height: "100%", backgroundColor: colors.greyDarkSecondary, opacity: "0.5" }} />
										<div style={{ display: "flex", alignItems: "start", justifyContent: "start", height: "18px", ..._valueStyle }}>{dayComponent.amount}</div>
									</div>
								)}
								</>
							}
						/>
					</div>
				);
			}

			return {
				...e,
				id: i,
				feedcurve_day_components: componentCell,
			}
		}) ?? [];
	}, [data]);

	return (
		<>
			<GenericDeviceTable
				title="Feed Days"
				columnsData={columnsData}
				data={tableData}
				loading={loading}
				error={error}
				onFetch={() => connectionState.deviceConnection && fetchData({ variables: connectionState.deviceConnection })}
				tabButtons={[
					<NewButton
						key="Reset table"
						content="Reset Table"
						startEnhancer={glyphs.ArrowReset({})}
						onClick={() => {
							setIsResetTableModalOpen(true);
						}}
						buttonType={ButtonTypes.tableButton}
						disabled={!connectionState.deviceConnection}
					/>,
					<NewButton
						key="Reset & clear"
						content="Reset & Clear"
						startEnhancer={glyphs.ArrowReset({})}
						onClick={() => {
							setIsResetTableClearQueueModalOpen(true);
						}}
						buttonType={ButtonTypes.tableButton}
						disabled={!connectionState.deviceConnection}
					/>
				]}
			/>
			{connectionState.deviceConnection && isResetTableModalOpen &&
				<ResetTableModal
					closeModal={()=>{ setIsResetTableModalOpen(false) }}
					onSuccess={() => fetchData({ variables: connectionState.deviceConnection })}
					tableId={LC_TABLE_IDS.feeddays}
				/>
			}
			{connectionState.deviceConnection && isResetTableClearQueueModalOpen &&
				<ResetTableClearQueueModal
					closeModal={()=>{ setIsResetTableClearQueueModalOpen(false) }}
					onSuccess={() => fetchData({ variables: connectionState.deviceConnection })}
					tableId={LC_TABLE_IDS.feeddays}
				/>
			}
		</>
	)
}